import React from 'react';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { CONTAINER_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { isEmbeddedWhiteboardMode } from '@confluence/whiteboard-common/entry-points/isEmbeddedWhiteboardMode.ts';
import { EmbeddedWhiteboardRouteWrapper } from '@confluence/whiteboard/entry-points/EmbeddedWhiteboardRouteWrapper.tsx';
import { fg } from '@confluence/feature-gating';

import { WhiteboardRouteComponentLoader } from './WhiteboardRouteComponentLoader.tsx';

const isEmbeddedWhiteboard = ({ query }: RouteMatch) =>
	isEmbeddedWhiteboardMode({
		parentProduct: query.parentProduct,
		presentationMode: query.presentationMode,
	});

export const WhiteboardRoute: {
	(match: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS(routeMatch: RouteMatch): {
		Screen: {
			screenEvent: {
				name: string;
				id: string;
				attributes: {
					contentId: string;
					focusedCommentId: string | string[] | undefined;
					focusedMentionId: string | string[] | undefined;
				};
			};
			pageState: {
				contentId: string;
				spaceKey: string;
				contentType: string;
				routeName: string;
			};
		};
		MainLayout:
			| boolean
			| {
					navView: string;
					spaceKey: string;
					isViewPage: boolean;
			  };
	};
} = (match: RouteMatch): React.JSX.Element => {
	const {
		params: { contentId, spaceKey },
	} = match;

	const element = <WhiteboardRouteComponentLoader spaceKey={spaceKey} contentId={contentId} />;

	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (isEmbeddedWhiteboard(match) && fg('confluence_ssr_embedded_whiteboard_preload_cache')) {
		return <EmbeddedWhiteboardRouteWrapper>{element}</EmbeddedWhiteboardRouteWrapper>;
	}

	return element;
};

WhiteboardRoute.NAVIGATION_PARAMS = ({
	name: routeName,
	params: { contentId, spaceKey },
	query: { focusedCommentId, focusedMentionId, parentProduct, presentationMode },
}: RouteMatch): {
	Screen: {
		screenEvent: {
			name: string;
			id: string;
			attributes: {
				contentId: string;
				focusedCommentId: string | string[] | undefined;
				focusedMentionId: string | string[] | undefined;
			};
		};
		pageState: {
			contentId: string;
			spaceKey: string;
			contentType: string;
			routeName: string;
		};
	};
	MainLayout:
		| boolean
		| {
				navView: string;
				spaceKey: string;
				isViewPage: boolean;
		  };
} => ({
	Screen: {
		screenEvent: {
			name: 'whiteboardScreen',
			id: contentId,
			attributes: {
				contentId,
				focusedCommentId,
				focusedMentionId,
			},
		},
		pageState: {
			contentId,
			spaceKey,
			contentType: 'whiteboard',
			routeName,
		},
	},
	MainLayout:
		isEmbeddedWhiteboardMode({ parentProduct, presentationMode }) &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		fg('confluence_ssr_embedded_whiteboard_preload_cache')
			? false
			: { navView: CONTAINER_HOME_ID, spaceKey, isViewPage: true },
});
